import React, { ReactNode } from 'react'
import './Advisor.scss'
import { getAdvisorLinkWithCookie } from './advisors-query-param'
import { APIENDPOINT, getCWPEnvironmentForURL, sanitizeHTML } from '../../../utils/common-utils'
import DOMPurify from 'dompurify'
/* =========================
 * Types
 * ========================= */

interface AdvisorUrls {
  site?: string
  referme?: string
}

interface AdvisorSocial {
  facebook?: string
  linkedin?: string
  // twitter?: string // currently disabled in original code
  [key: string]: string | undefined
}

type AdvisorType = 'advisor' | 'team'

export interface AdvisorData {
  type: AdvisorType
  name?: string
  image?: string
  email?: string
  phone?: string
  title?: string // may include HTML (sanitized)
  designations?: string // may include HTML (sanitized)
  urls: AdvisorUrls
  social: AdvisorSocial
}

export interface Labels {
  [key: string]: unknown
}

interface PhoneNumberProps {
  phone: string
}

interface AdvisorImageNoLinkProps {
  data: AdvisorData
  labels: Labels
}

interface SocialIconProps {
  icon: string
  url: string
}

interface SocialIconsProps {
  data: AdvisorData
}

interface ReferralLinkProps {
  data: AdvisorData
  labels: Labels
}

interface SoloAdvisorProps {
  data: AdvisorData
  labels: Labels
}

interface TeamAdvisorProps {
  data: AdvisorData
}

export interface ClientProps {
  data?: AdvisorData
  labels: Labels
}

/* =========================
 * Components
 * ========================= */

function PhoneNumber({ phone }: PhoneNumberProps) {
  return <a href={DOMPurify.sanitize(`tel:+1${phone}`)}>{phone}</a>
}

function AdvisorImageNoLink({ data, labels }: AdvisorImageNoLinkProps) {
  const env = getCWPEnvironmentForURL(window.location.href)
  const src =
    data.image ||
    `${APIENDPOINT(env).baseGallery}/content/gallery/wealth-management/${labels.missingImage}`
  const alt = data.name || 'Missing Image'
  return <img src={DOMPurify.sanitize(src)} alt={alt} className='Advisor-image u-maxWidth100' />
}

/**
 * Helper function (not a React component) to render the H3 with dynamic classNames.
 * Kept as a function to match the original invocation style.
 */
function AdvisorNameNoLink(data: AdvisorData, classNames?: string) {
  return <h3 className={classNames}>{data.name}</h3>
}

function SocialIcon({ icon, url }: SocialIconProps) {
  const icons: Record<string, string> = {
    // twitter: 'social-twitter', // disabling twitter for now due to icon change
    facebook: 'social-facebook',
    linkedin: 'social-linkedin',
  }

  if (!icons[icon]) return null

  return (
    <li key={icon} className='u-textSizeMsrp1 u-flexJustifyCenter u-inlineBlock u-marginRight16'>
      <a
        href={DOMPurify.sanitize(url)}
        target='_blank'
        rel='noopener noreferrer'
        className='Icon-link BrandIconsComponent'
      >
        <svg
          aria-hidden='true'
          className='BrandIconsComponent--icon BrandIcon--default u-sizeXSmall u-textSizeMsrp3'
        >
          <use
            href={DOMPurify.sanitize(
              `/binaries/content/gallery/ampmicrosites/brand-icons/icon-sprite.svg#${icons[icon]}`
            )}
          />
        </svg>
        <span className='u-hiddenVisually'>{icon}</span>
      </a>
    </li>
  )
}

function SocialIcons({ data }: SocialIconsProps) {
  const social = data.social || {}
  const items = Object.keys(social)
    .filter(key => Boolean(social[key]))
    .map(icon => <SocialIcon key={icon} icon={icon} url={social[icon]!} />)

  if (items.length === 0) return null
  return <ul className='u-border0'>{items}</ul>
}

function ReferralLink({ data, labels }: ReferralLinkProps) {
  const referLink = data.type === 'team' ? data.urls.site : data.urls.referme
  if (!referLink || !labels.referLabel) return null

  const referLinkWithAuth = getAdvisorLinkWithCookie(referLink)
  return (
    <a
      className='AdvisorClient-refer Button Button--secondary Button--secondary--action-blue u-textColorActionBlue u-flexAlignItemsCenter u-flexJustifyCenter u-marginTop20 u-md-sizeFull u-sm-sizeFull'
      href={DOMPurify.sanitize(referLinkWithAuth as string)}
      target='_blank'
      rel='noopener noreferrer'
    >
      {labels.referLabel as ReactNode}
    </a>
  )
}

function SoloAdvisor({ data, labels }: SoloAdvisorProps): React.JSX.Element {
  const email = data.email && <p className='u-marginBottom8 u-textBreak'>{data.email}</p>
  const phone = data.phone && <p className='u-marginBottom8'>{data.phone}</p>

  return (
    <div>
      <div className='AdvisorClient AdvisorClient--nextGen2024 Grid Card-body u-padding20'>
        <div className='u-size8of12 u-flex u-flexRow u-paddingRight20 u-md-sizeFull u-sm-sizeFull u-sm-flexCol u-md-paddingRight0 u-sm-paddingRight0'>
          <div className='AdvisorClient-image u-marginRight20 u-sm-marginRight0 u-sm-marginBottom20'>
            <AdvisorImageNoLink data={data} labels={labels} />
          </div>
          <div className='AdvisorClient-info'>
            <h5 className='AdvisorClient-advisor Heading--withSeparator u-textUppercase u-textColorMidnightBlue'>
              {labels?.advisorLabel as ReactNode}
            </h5>

            {AdvisorNameNoLink(
              data,
              'AdvisorClient-name u-textSecondary u-marginBottom4 u-inlineBlock'
            )}

            {data.designations && (
              <p
                className='u-md-marginVert8 u-sm-marginVert8'
                dangerouslySetInnerHTML={{
                  __html: sanitizeHTML(data.designations),
                }}
              />
            )}

            {data.title && (
              <p
                dangerouslySetInnerHTML={{
                  __html: sanitizeHTML(data.title),
                }}
              />
            )}

            <div className='u-md-hidden u-sm-hidden'>
              <ReferralLink data={data} labels={labels} />
            </div>
          </div>
        </div>

        <div className='u-lg-hidden u-size12of12'>
          <ReferralLink data={data} labels={labels} />
        </div>

        <div className='u-size4of12 u-paddingLeft20 u-borderLeft u-md-sizeFull u-md-paddingLeft0 u-md-paddingTop20 u-md-borderLeft0 u-md-marginTop20 u-md-borderTop u-sm-sizeFull u-sm-paddingLeft0 u-sm-paddingTop20 u-sm-marginTop20 u-sm-borderLeft0 u-sm-borderTop'>
          <div className='u-textPrimary u-textColorNeutralGrey u-textBold u-marginBottom8'>
            Contact
          </div>

          <ul className='AdvisorClient-contactInfo List Grid'>
            {email && (
              <li className='List-item u-sizeFull u-flex u-flexAlignItemsCenter u-marginBottom4 u-md-size6of12 u-md-marginBotton2gu u-sm-marginBottom8'>
                <span
                  className='adel-material-symbols u-textColorActionBlue u-marginRight8'
                  role='img'
                  title='mail'
                  aria-label='mail'
                >
                  mail
                </span>
                <a className='u-textTruncate' href={DOMPurify.sanitize(`mailto:${data.email}`)}>
                  {data.email}
                </a>
              </li>
            )}

            {phone && (
              <li className='List-item u-sizeFull u-flex u-flexAlignItemsCenter u-marginBottom4 u-md-size6of12 u-md-marginBotton2gu u-sm-marginBottom8'>
                <span
                  className='adel-material-symbols u-textColorActionBlue u-marginRight8'
                  role='img'
                  title='phone'
                  aria-label='phone'
                >
                  phone
                </span>
                <a href={DOMPurify.sanitize(`tel:${data.phone}`)}>{data.phone}</a>
              </li>
            )}

            {data.urls.site && (
              <li className='List-item u-sizeFull u-flex u-flexAlignItemsCenter u-marginBottom4 u-md-size6of12 u-md-marginBotton2gu u-sm-marginBottom8'>
                <span
                  className='adel-material-symbols u-textColorActionBlue u-marginRight8'
                  role='img'
                  title='new_window'
                  aria-label='new_window'
                >
                  new_window
                </span>
                <a href={DOMPurify.sanitize(data.urls.site)} target='_blank' rel='noreferrer'>
                  Advisor website
                </a>
              </li>
            )}

            <li className='List-item u-sizeFull u-md-size6of12'>
              <SocialIcons data={data} />
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}

function TeamAdvisor({ data }: TeamAdvisorProps): React.JSX.Element {
  return (
    <div className='Grid Grid--fit Grid--withGutter Content u-padding32'>
      <div>
        <h3 className='u-paddingBottom20'>Your advisor team</h3>
        <div className='Grid Grid--fit Grid--withGutter Content'>
          <div className='Content Content--large u-sm-marginBottom32 u-md-marginBottom32'>
            {data.urls?.site && data.name && (
              <p>
                <span className='u-textBold u-textSizeMsrp1'>
                  <a href={DOMPurify.sanitize(data.urls.site)}>{data.name}</a>
                </span>
              </p>
            )}
            <br />
            {data.phone && <PhoneNumber phone={data.phone} />}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Client({ data, labels }: ClientProps): React.JSX.Element {
  if (data) {
    if (data.type === 'advisor') {
      return <SoloAdvisor data={data} labels={labels} />
    }
    if (data.type === 'team') {
      return <TeamAdvisor data={data} />
    }
  }
  return <div />
}
